/**
 * This component renders a modal popup to display a list of problematic
 * materials that require user attention. It supports two types of materials:
 * 
 * 1. **Moved Materials** - Materials that have been shifted from their
 *    original catalog.
 * 2. **Broken Materials** - Materials that are either hidden (`hide_search = true`)
 *    or inactive (`status = INACTIVE`).
 * 
 * The modal displays a serial number, item name (linked to edit page), and
 * material status in a tabular format. Users can take further action by
 * clicking the "Edit Template" button.
 *
 * PROPS:
 * - `show` (Boolean): Controls modal visibility.
 * - `onClose` (Function): Callback triggered when the modal is closed.
 * - `materials` (Array | Object): 
 *    - For type `'moved'`: Array of moved material objects.
 *    - For type `'hidden'`: Object containing arrays `hiddenMaterials` and `inactiveMaterials`.
 * - `type` (String): Type of material list shown: either `'moved'` or `'hidden'`.
 * - `onEditTemplate` (Function): Callback triggered when the "Edit Template" button is clicked.
 *
 * FEATURES:
 * - Table with clickable item names that open the material edit page in a new tab.
 * - Dynamic table data generation based on material type.
 * - User-friendly fallback when no data is available.
 *
 * USAGE:
 * Used within templates or material management modules to help users 
 * identify and resolve issues with catalog materials.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CATALOGS, MATERIAL } from '../../constants/route-paths';
import Modal from '../../components/modal/modal';
import CustomTable from '../../components/table/table';
import { toUSD } from 'src/util/currency-formatter';

function getModalHeading(type) {
  switch (type) {
    case 'moved':
      return 'Moved Materials';
    case 'differentAccount':
      return 'Materials from Different User Account';
    case 'hidden':
      return 'Broken Materials';
    case 'duplicate':
      return 'Duplicate Materials';
    case 'all':
      return 'Template Health Check - All Issues';
    default:
      return 'Materials';
  }
}
function MovedMaterialsModal({ show, onClose, materials = [], type = 'moved', onEditTemplate }) {
  const columns = [
    {
      title: 'S.No',
      field: 'serial',
    },
    {
      title: 'Item Name',
      field: 'item',
      render: (item) => (
        <Link
          to={`/${CATALOGS}/${MATERIAL}/edit/${item.id}`}
          className="text-primary text-decoration-underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.item || item.name} ({item.partNo})
        </Link>
      ),
    },
    {
      title: 'Cost',
      field: 'cost',
      render: (item) => <>{item.cost ? `${toUSD(item.cost)}` : '-'}</>,
    },
    ...(type === 'all' ? [{
      title: 'Issue Type',
      field: 'status',
      render: (item) => {
        let badgeClass = 'badge ';
        let text = item.status;
        
        switch (item.status) {
          case 'Hidden':
            badgeClass += 'bg-warning text-dark';
            break;
          case 'InActive':
            badgeClass += 'bg-danger text-white';
            break;
          case 'Different Account':
            badgeClass += 'bg-danger text-white';
            break;
          case 'Duplicate Item':
            badgeClass += 'bg-warning text-dark';
            break;
          default:
            badgeClass += 'bg-secondary text-white';
        }
        
        return <span className={badgeClass}>{text}</span>;
      },
    }] : [{
      title: 'Status',
      field: 'status',
      render: (item) => <span className="text-uppercase">{item.status}</span>,
    }]),
  ];

  let tableData = [];

  if (type === 'hidden') {
    const hidden = (materials.hiddenMaterials || []).map((mat) => ({ ...mat, status: 'Hidden' }));
    const inactive = (materials.inactiveMaterials || []).map((mat) => ({ ...mat, status: 'InActive' }));
    tableData = [...hidden, ...inactive].map((mat, i) => ({ ...mat, serial: i + 1 }));
  } else if (type === 'moved') {
    tableData = materials.map((mat, i) => ({ ...mat, serial: i + 1 }));
  } else if (type === 'differentAccount') {
    const diffAcc = (materials.differentAccountMaterials || []).map((mat) => ({ ...mat, status: 'Different Account' }));
    tableData = diffAcc.map((mat, i) => ({ ...mat, serial: i + 1 }));
  } else if (type === 'duplicate') {
    const duplicate = (materials.duplicateMaterials || []).map((mat) => ({ 
      ...mat, 
      status: 'Duplicate Item',
      duplicates: mat.duplicates || []
    }));
    tableData = duplicate.map((mat, i) => ({ ...mat, serial: i + 1 }));
  } else if (type === 'all') {
    // Combine all material types with their respective statuses
    const hidden = (materials.hiddenMaterials || []).map((mat) => ({ ...mat, status: 'Hidden' }));
    const inactive = (materials.inactiveMaterials || []).map((mat) => ({ ...mat, status: 'InActive' }));
    const differentAccount = (materials.differentAccountMaterials || []).map((mat) => ({ ...mat, status: 'Different Account' }));
    const duplicate = (materials.duplicateMaterials || []).map((mat) => ({ 
      ...mat, 
      status: 'Duplicate Item',
      duplicates: mat.duplicates || []
    }));
    
    // Sort by status priority: Different Account > InActive > Hidden > Duplicate Item
    const allMaterials = [...hidden, ...inactive, ...differentAccount, ...duplicate];
    allMaterials.sort((a, b) => {
      const priority = { 'Different Account': 1, 'InActive': 2, 'Hidden': 3, 'Duplicate Item': 4 };
      return (priority[a.status] || 5) - (priority[b.status] || 5);
    });
    
    tableData = allMaterials.map((mat, i) => ({ ...mat, serial: i + 1 }));
  }
  return (
    <Modal
      open={show}
      onClose={onClose}
      modalHeading={getModalHeading(type)}
      modalContent={
        tableData.length > 0 ? (
          <CustomTable
            columns={columns}
            data={tableData}
            toolbar={false}
            exportButton={false}
            addButton={false}
            isEdit={false}
            isDelete={false}
            isAction={false}
            paginationCount={tableData.length}
            enableSearch={false}
            itemsPerPageCustom={false}
            stickyHeader={false}
          />
        ) : (
          <p className="text-center">No {type === 'moved' ? 'moved' : type === 'duplicate' ? 'duplicate' : type === 'all' ? 'issues' : 'broken'} materials found.</p>
        )
      }
      modalCloseTxt="Close"
      modalSaveTxt="Edit Template"
      onClick={onEditTemplate}
      showActionBtn={true}
      maxWidth="lg"
    />
  );
}

export default MovedMaterialsModal;